import React, { useCallback, useState } from "react";
import dayjs from "dayjs";
import { FiMapPin, FiClock, FiFileText, FiFlag } from "react-icons/fi";
import ReportModal from "./ReportModal";
import CoverageProgressReport from "./CoverageProgressReport";
import TimelinessReport from "./TimelinessReport";
import ReportingQualityReport from "./ReportingQualityReport";
import RedFlagsReport from "./RedFlagsReport";
import "./reports.scss";
import { HOME_COLORS } from "../homeTheme";

// F — Home "Generate Reports": four monitoring reports for government/donor
// oversight. Each box opens a modal with high-level KPIs + charts and can be
// downloaded as Word or PDF.
const REPORTS = [
  {
    key: "coverage",
    title: "Coverage & Progress",
    description: "Planned vs completed, and where the rest stand.",
    color: HOME_COLORS.info,
    Icon: FiMapPin,
    Body: CoverageProgressReport,
  },
  {
    key: "timeliness",
    title: "Deadlines & Timeliness",
    description: "On-time completion and the average delay in days.",
    color: HOME_COLORS.ok,
    Icon: FiClock,
    Body: TimelinessReport,
  },
  {
    key: "reporting",
    title: "Reporting & Data Quality",
    description: "Writing, submitted, approved, delivered.",
    color: HOME_COLORS.brand,
    Icon: FiFileText,
    Body: ReportingQualityReport,
  },
  {
    key: "flags",
    title: "Red Flags & Accountability",
    description: "Issues raised during monitoring, and what was resolved.",
    color: HOME_COLORS.alert,
    Icon: FiFlag,
    Body: RedFlagsReport,
  },
];

const ReportsPanel = () => {
  const [activeKey, setActiveKey] = useState(null);
  const [kpis, setKpis] = useState([]);
  const [bodyLoading, setBodyLoading] = useState(true);

  const projectRoundName = localStorage.getItem("projectRoundName");
  const meta = `${projectRoundName || "Project"} · Generated ${dayjs().format(
    "DD MMM YYYY"
  )}`;

  const openReport = (key) => {
    setKpis([]);
    setBodyLoading(true);
    setActiveKey(key);
  };
  const closeReport = () => setActiveKey(null);

  // Stable callbacks so the body's effects don't re-fire on every render.
  const handleReady = useCallback((k) => setKpis(k), []);
  const handleLoading = useCallback((l) => setBodyLoading(l), []);

  const active = REPORTS.find((r) => r.key === activeKey);
  const ActiveBody = active?.Body;

  return (
    <div className="reportsDiv">
      <div className="reportsHead">
        <h2>Reports</h2>
        <span className="reportsHeadHint">
          Monitoring reports — open one to read it, export as Word or PDF
        </span>
      </div>

      <div className="reportsGrid">
        {REPORTS.map((r) => {
          const Icon = r.Icon;
          return (
            <button
              type="button"
              key={r.key}
              className="reportBox"
              onClick={() => openReport(r.key)}
            >
              <span className="reportBoxIcon" style={{ color: r.color }}>
                <Icon />
              </span>
              <span className="reportBoxText">
                <span className="reportBoxTitle">{r.title}</span>
                <span className="reportBoxDesc">{r.description}</span>
              </span>
            </button>
          );
        })}
      </div>

      <ReportModal
        open={!!active}
        onClose={closeReport}
        title={active?.title}
        meta={meta}
        color={active?.color}
        icon={active ? <active.Icon /> : null}
        loading={bodyLoading}
        kpis={kpis}
      >
        {ActiveBody && (
          <ActiveBody
            key={active.key}
            onReady={handleReady}
            onLoadingChange={handleLoading}
          />
        )}
      </ReportModal>
    </div>
  );
};

export default ReportsPanel;
